/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchAllConfigs,
  fetchProviderTemplate, 
  createStorageConfig, 
  updateConfigValue,
  clearConfigError 
} from '../store/slices/storageConfigSlice';
import { fetchBranchesByCompanyId } from '../store/slices/branchSlice'; 
import Layout from '../common/components/Layout';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const StorageConfig = () => {
  const dispatch = useDispatch();
  const localCompanyId = localStorage.getItem("companyId") || "";
  const userRole = localStorage.getItem("role") || "";

  // Redux State
  const { configs = [], templates = {} } = useSelector(state => state.storageConfig || {});
  const branchState = useSelector(state => state.branch);
  const branches = branchState?.list || []; 
  
  /* ===================== LOCAL STATE ===================== */
  const [searchTerm, setSearchTerm] = useState("");
  const [levelFilter, setLevelFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isFetchingTemplate, setIsFetchingTemplate] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    id: '', provider: '', level: 'company', branchId: '', isActive: false, configValue: {} 
  });

  useEffect(() => {
    if (localCompanyId) {
      dispatch(fetchAllConfigs(localCompanyId));
      dispatch(fetchBranchesByCompanyId({ companyId: localCompanyId, role: userRole }));
    }
  }, [dispatch, localCompanyId, userRole]);

  /* ===================== FILTER LOGIC ===================== */
  const filteredConfigs = configs.filter(item => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      item.provider?.toLowerCase().includes(searchLower) || 
      item.branch?.branchName?.toLowerCase().includes(searchLower);
    const matchesLevel = levelFilter === "all" || item.level === levelFilter;
    return matchesSearch && matchesLevel;
  });

  /* ===================== LOGIC HANDLERS ===================== */
  const handleOpenAdd = () => {
    dispatch(clearConfigError());
    setIsEditMode(false);
    setFormData({ id: '', provider: '', level: 'company', branchId: '', isActive: false, configValue: {} });
    setShowModal(true);
  };

  const handleOpenEdit = async (item) => {
    dispatch(clearConfigError());
    setIsEditMode(true);
    setIsFetchingTemplate(true);
    await dispatch(fetchProviderTemplate(item.provider));
    setIsFetchingTemplate(false);

    setFormData({
      id: item.id,
      provider: item.provider,
      level: item.level || 'company',
      branchId: item.branch?.id || item.branchId || '', 
      isActive: item.isActive,
      configValue: item.configValue || {}
    });
    setShowModal(true);
  };

  const handleStatusToggle = async (item) => {
    try {
      await dispatch(updateConfigValue({ id: item.id, isActive: !item.isActive, companyId: localCompanyId })).unwrap();
      toast.success("Status updated!");
      dispatch(fetchAllConfigs(localCompanyId));
    } catch (err) {
      toast.error("Update failed");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const actionPayload = { ...formData, companyId: localCompanyId, branchId: formData.level === 'branch' ? formData.branchId : null };
      if (isEditMode) {
        await dispatch(updateConfigValue(actionPayload)).unwrap();
      } else {
        await dispatch(createStorageConfig(actionPayload)).unwrap();
      }
      setShowModal(false);
      dispatch(fetchAllConfigs(localCompanyId));
      toast.success("Saved successfully");
    } catch (err) {
      toast.error(err?.message || "Error saving");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Layout>
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="container-fluid py-4 flex-grow-1 px-lg-5">
        <style>{`
          .custom-table-header th { background-color: #0f172a !important; color: #fff !important; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; }
          .storage-card { background: #fff; border-radius: 12px; border: 1px solid rgba(0,0,0,0.05); }
          .compact-filter { height: 38px; font-size: 0.875rem; border-radius: 6px; }
          .search-wrapper { position: relative; width: 250px; }
          .search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #6c757d; }
          .search-input { padding-left: 32px; }
        `}</style>

        {/* Header & Compact Filters Row */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
          <div>
            <h2 className="fw-bold m-0 text-dark">Storage Configurations</h2>
            <p className="text-muted small mb-0">Manage global or branch-specific storage</p>
          </div>

          <div className="d-flex gap-2">
            <div className="search-wrapper">
              <i className="bi bi-search search-icon"></i>
              <input 
                type="text" className="form-control compact-filter search-input" 
                placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select 
              className="form-select compact-filter" style={{ width: '160px' }}
              value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)}
            >
              <option value="all">All Levels</option>
              <option value="company">Company</option>
              <option value="branch">Branch</option>
            </select>
            <button className="btn btn-dark compact-filter px-3" onClick={handleOpenAdd}>
              <i className="bi bi-plus-lg me-1"></i> Add Provider
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="storage-card shadow-sm p-0 overflow-hidden">
          <table className="table table-hover align-middle mb-0">
            <thead className="custom-table-header">
              <tr>
                <th className="ps-4 py-3">Storage Type</th>
                <th className="py-3">Apply To</th>
                <th className="py-3">Company</th>
                <th className="py-3">Date Added</th>
                <th className="py-3 text-center">Status</th>
                <th className="pe-4 py-3 text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredConfigs.length > 0 ? filteredConfigs.map(item => (
                <tr key={item.id}>
                  <td className="ps-4 fw-bold">{item.provider?.toUpperCase()}</td>
                  <td><span className="badge bg-light text-dark border">{item.level === 'company' ? 'Company' : 'Branch'}</span></td>
                  <td><span className="badge bg-light text-dark border">{item.level === 'company' ? item.company.companyName : item.branch.branchName}</span></td>
                  <td className="text-muted small">{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '---'}</td>
                  <td className="text-center">
                    <div className="form-check form-switch d-flex justify-content-center">
                      <input className="form-check-input" type="checkbox" checked={item.isActive} onChange={() => handleStatusToggle(item)} />
                    </div>
                  </td>
                  <td className="pe-4 text-end">
                    <button className="btn btn-sm btn-outline-dark" onClick={() => handleOpenEdit(item)}>Manage</button>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan="6" className="text-center py-5 text-muted">No records found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal - Kept consistent with your logic */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1050 }}>
          <div className="bg-white p-4 rounded-4 shadow-lg" style={{ width: '450px', maxWidth: '95%' }}>
            <h5 className="fw-bold mb-3">{isEditMode ? 'Edit' : 'Add'} Storage</h5>
            <form onSubmit={handleSave}>
              <div className="mb-3">
                <label className="small fw-bold text-muted">STORAGE SERVICE</label>
                <select className="form-select mt-1" value={formData.provider} onChange={(e) => {
                  const p = e.target.value;
                  setFormData({...formData, provider: p});
                  if(p) dispatch(fetchProviderTemplate(p));
                }} disabled={isEditMode} required>
                  <option value="">-- Select --</option>
                  <option value="s3">Amazon S3</option>
                  <option value="zoho">Zoho WorkDrive</option>
                </select>
              </div>

              <div className="mb-3">
                <label className="small fw-bold text-muted">APPLY TO</label>
                <select className="form-select mt-1" value={formData.level} onChange={(e) => setFormData({...formData, level: e.target.value, branchId: ''})}>
                  <option value="company">Entire Company</option>
                  <option value="branch">Specific Branch</option>
                </select>
              </div>

              {formData.level === 'branch' && (
                <div className="mb-3">
                  <label className="small fw-bold text-muted">BRANCH NAME</label>
                  <select className="form-select mt-1" value={formData.branchId} onChange={(e) => setFormData({...formData, branchId: e.target.value})} required>
                    <option value="">-- Choose Branch --</option>
                    {branches.map(b => <option key={b.id} value={b.id}>{b.branchName}</option>)}
                  </select>
                </div>
              )}

              {formData.provider && templates[formData.provider] && (
                <div className="row g-2">
                  {Object.keys(templates[formData.provider]).map((key) => (
                    <div className="col-12" key={key}>
                      <label className="small text-muted text-capitalize">{key.replace(/([A-Z])/g, ' $1')}</label>
                      <input 
                        type={(key.toLowerCase().includes('secret') || key.toLowerCase().includes('key')) ? "password" : "text"}
                        className="form-control mt-1"
                        value={formData.configValue[key] || ''}
                        onChange={(e) => setFormData(prev => ({...prev, configValue: {...prev.configValue, [key]: e.target.value}}))}
                        required
                      />
                    </div>
                  ))}
                </div>
              )}

              <div className="d-flex gap-2 mt-4 pt-3 border-top">
                <button type="button" className="btn btn-light w-100" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-dark w-100" disabled={isSaving}>{isSaving ? 'Saving...' : 'Save Settings'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default StorageConfig;